import { updateTeamSchema, type Team } from '@nexo/types';
import { Button, Field, Input, Modal, Switch, Textarea, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, LogOut, Trash2 } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../lib/api';
import { errorMessage, errorsFromApi, useForm, validate } from '../../lib/forms';
import { can } from '../../lib/permissions';
import { keys, useFeatures } from '../../lib/queries';
import { ImportSection } from './ImportSection';

/** Team name, public listing and description; leaving or deleting the team. */
export function TeamSettingsDialog({
  team,
  open,
  onClose,
}: {
  team: Team;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const features = useFeatures();
  const canManage = can(team, 'manage_team');
  const form = useForm({
    name: team.name,
    description: team.description ?? '',
    isPublic: team.isPublic,
  });

  const save = useMutation({
    mutationFn: (input: Parameters<typeof api.teams.update>[1]) => api.teams.update(team.id, input),
    onSuccess: (updated) => {
      queryClient.setQueryData<Team[]>(keys.teams, (list) =>
        list?.map((other) => (other.id === updated.id ? updated : other)),
      );
      toast({ title: t('Equipo actualizado'), tone: 'success' });
      onClose();
    },
    onError: (err) => {
      const fieldErrors = errorsFromApi(err);
      if (fieldErrors) form.setErrors(fieldErrors);
      else toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' });
    },
  });

  function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const { data, errors } = validate(updateTeamSchema, {
      name: form.values.name,
      description: form.values.description.trim() || null,
      isPublic: form.values.isPublic,
    });
    if (errors) return form.setErrors(errors);
    save.mutate(data);
  }

  return (
    <Modal open={open} onClose={onClose} title={t('Ajustes de {{team}}', { team: team.name })}>
      {canManage ? (
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <Field label={t('Nombre')} error={form.errors.name}>
            {(props) => (
              <Input
                {...props}
                value={form.values.name}
                onChange={(e) => form.set('name', e.target.value)}
              />
            )}
          </Field>
          <Field
            label={t('Descripción')}
            error={form.errors.description}
            hint={t('Se muestra en «Explorar servidores» si el equipo es público.')}
          >
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                maxLength={200}
                value={form.values.description}
                onChange={(e) => form.set('description', e.target.value)}
              />
            )}
          </Field>
          {(features.data?.publicDirectory || team.isPublic) && !team.official && (
            <Switch
              icon={<Globe />}
              label={t('Servidor público')}
              hint={t('Aparece en «Explorar servidores» y cualquiera con cuenta puede entrar.')}
              checked={form.values.isPublic}
              onChange={(value) => form.set('isPublic', value)}
            />
          )}
          <Button type="submit" loading={save.isPending} className="self-end">
            {t('Guardar cambios')}
          </Button>
        </form>
      ) : (
        <p className="text-sm text-muted">
          {t('Solo quien administra el equipo puede cambiar sus ajustes.')}
        </p>
      )}
      {canManage && <ImportSection team={team} />}
      <DangerZone team={team} onDone={onClose} />
    </Modal>
  );
}

function DangerZone({ team, onDone }: { team: Team; onDone: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { teamId } = useParams();
  const [confirming, setConfirming] = useState('');
  const isOwner = team.role === 'owner';

  const gone = () => {
    queryClient.setQueryData<Team[]>(keys.teams, (list) =>
      list?.filter((other) => other.id !== team.id),
    );
    onDone();
    if (teamId === team.id) void navigate('/app');
  };
  const leave = useMutation({
    mutationFn: () => api.teams.leave(team.id),
    onSuccess: gone,
    onError: (err) =>
      toast({ title: t('No puedes salir'), description: errorMessage(err), tone: 'error' }),
  });
  const remove = useMutation({
    mutationFn: () => api.teams.delete(team.id),
    onSuccess: gone,
    onError: (err) =>
      toast({ title: t('No se pudo eliminar'), description: errorMessage(err), tone: 'error' }),
  });

  return (
    <section className="mt-6 border-t border-border pt-4">
      {isOwner ? (
        <>
          <h3 className="text-sm font-semibold text-danger">{t('Eliminar el equipo')}</h3>
          <p className="mt-1 text-sm text-muted">
            {t(
              'Se borran sus canales, mensajes, archivos, tareas y sprints para todos. Escribe el nombre del equipo para confirmar.',
            )}
          </p>
          <div className="mt-3 flex gap-2">
            <Input
              aria-label={t('Nombre del equipo')}
              placeholder={team.name}
              value={confirming}
              onChange={(e) => setConfirming(e.target.value)}
            />
            <Button
              variant="danger"
              icon={<Trash2 className="size-4" />}
              disabled={confirming.trim() !== team.name}
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {t('Eliminar')}
            </Button>
          </div>
        </>
      ) : (
        <Button
          variant="ghost"
          icon={<LogOut className="size-4" />}
          className="text-danger"
          loading={leave.isPending}
          onClick={() => leave.mutate()}
        >
          {t('Salir del equipo')}
        </Button>
      )}
    </section>
  );
}
